import { LocalizedString } from "../types/entities";

export type LocalizedValue =
  | LocalizedString
  | string
  | null
  | undefined;

const SUPPORTED_LANGUAGES = ["pl", "en", "uk", "de", "ko"] as const;

type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const normalizeLanguage = (language?: string): SupportedLanguage => {
  const lang = language?.split("-")[0]?.toLowerCase();

  if (lang && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
    return lang as SupportedLanguage;
  }
  return "pl";
};

const cleanValue = (val: unknown): string | null => {
  if (typeof val !== "string") return null;
  const trimmed = val.trim();
  if (!trimmed || trimmed === "-" || trimmed === "_" || trimmed === "—") {
    return null;
  }
  return trimmed;
};

export const parseLocalizedValue = (
  value: LocalizedValue
): LocalizedString | string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    try {
      const parsed: unknown = JSON.parse(trimmed);

      if (
        parsed !== null &&
        typeof parsed === "object" &&
        !Array.isArray(parsed)
      ) {
        return parsed as LocalizedString;
      }

      if (typeof parsed === "string") {
        return parsed;
      }
    } catch {
      // Invalid JSON is treated as a normal string.
    }
  }
  return value;
};

export const getLocalized = (
  value: LocalizedValue,
  language?: string
): string => {
  // If the value is an object (not an array), we try to find the appropriate localized string based on the provided language or fall back to other languages.
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const lang = normalizeLanguage(language);
    const obj = value as Record<string, unknown>;
    
    // We look for the value in the order of the specified language, then fall back to Polish, English, Ukrainian, German, Korean, and finally any string value found in the object.
    const found = obj[lang] || obj.pl || obj.en || obj.uk || obj.de || obj.ko || Object.values(obj).find(v => typeof v === "string");
    if (typeof found === "string") {
      return cleanValue(found) ?? "";
    }
    return "";
  }

  const parsed = parseLocalizedValue(value);

  if (parsed === null) {
    return "";
  }

  if (typeof parsed === "string") {
    return cleanValue(parsed) ?? "";
  }

  const lang = normalizeLanguage(language);

  const candidates = [
    parsed[lang],
    parsed.pl,
    parsed.en,
    parsed.uk,
    parsed.de,
    parsed.ko,
    ...Object.values(parsed),
  ];

  for (const item of candidates) {
    if (typeof item === "string") {
      const cleaned = cleanValue(item);
      if (cleaned !== null) {
        return cleaned;
      }
    }
  }

  return "";
};

// En: This function is a utility that takes a value (which can be a localized string object, a simple string, or null/undefined) and a language code. It returns the appropriate localized string based on the provided language, falling back to other languages if necessary. If no valid string is found, it returns an empty string.
export function parseLocalizedString(value: any, language: string = "pl"): string {
  return getLocalized(value, language);
}